import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AIStudentPerformanceAnalyticsProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AIStudentPerformanceAnalytics: React.FC<AIStudentPerformanceAnalyticsProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'AI Student Performance Analytics & Prediction | My Learning Board';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'AI-powered student performance analytics that predicts academic outcomes, identifies at-risk students early, and provides personalized improvement plans. Real-time dashboards, trend analysis & parent reports.');
    }
    return () => { document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India'; };
  }, []);

  const analyticsFeatures = [
    { icon: 'bi-graph-up-arrow', title: 'Performance Prediction', desc: 'AI predicts end-of-term and board exam results from early assessments with 85%+ accuracy. Intervene before it\'s too late.', color: '#6610F2' },
    { icon: 'bi-exclamation-triangle', title: 'At-Risk Detection', desc: 'AI flags students showing declining trends — dropping grades, attendance patterns, and behavioral signals combined.', color: '#E53935' },
    { icon: 'bi-person-lines-fill', title: 'Individual Learning Profile', desc: 'Every student gets an AI-generated profile showing strengths, weaknesses, learning pace and recommended focus subjects.', color: '#2D7DD2' },
    { icon: 'bi-bar-chart-line', title: 'Comparative Analytics', desc: 'Compare performance across sections, subjects, terms and years. Identify which teaching methods produce the best outcomes.', color: '#28A745' },
    { icon: 'bi-clipboard-data', title: 'Parent Insight Reports', desc: 'AI generates easy-to-understand reports for parents — no jargon, just clear insights on their child\'s progress and next steps.', color: '#FB8C00' },
    { icon: 'bi-lightbulb', title: 'Smart Recommendations', desc: 'AI suggests specific actions: "Ravi needs 5 more hours on Trigonometry to move from C+ to B+ based on his learning pattern."', color: '#D81B60' },
  ];

  const dashboardMetrics = [
    { label: 'Class Average Trend', value: '78.5%', trend: '+2.3%', up: true, icon: 'bi-graph-up' },
    { label: 'At-Risk Students', value: '12', trend: '-3', up: false, icon: 'bi-exclamation-circle' },
    { label: 'Top Performers', value: '45', trend: '+8', up: true, icon: 'bi-star' },
    { label: 'Attendance Correlation', value: '0.87', trend: 'Strong', up: true, icon: 'bi-link-45deg' },
  ];

  const useCases = [
    {
      title: 'Early Warning System',
      scenario: 'Class 10 student Priya scored 82% in Unit Test 1 but dropped to 65% in Unit Test 2.',
      aiAction: 'AI detects the 17-point drop, flags Priya as at-risk, identifies Mathematics and Science as declining subjects, and notifies her class teacher and parents with a suggested intervention plan.',
      impact: 'Teacher provides extra support. Priya recovers to 78% by Mid-Term.',
    },
    {
      title: 'Subject-Teacher Effectiveness',
      scenario: 'Section A averages 75% in Physics while Section B averages 58% — same syllabus, different teachers.',
      aiAction: 'AI highlights the gap, analyzes teaching patterns, and recommends knowledge-sharing sessions between teachers. Also identifies specific topics where Section B struggles.',
      impact: 'Section B improves by 12% after targeted intervention.',
    },
    {
      title: 'Board Exam Readiness',
      scenario: 'School needs to predict Class 12 board results based on internal assessments.',
      aiAction: 'AI builds prediction models from 3 years of historical data. Generates readiness scores for each student, identifying those likely to score below 60% in board exams.',
      impact: '95% prediction accuracy. Targeted coaching improves pass rate by 8%.',
    },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #5C2597 0%, #6610F2 50%, #8E24AA 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-dark mb-3 px-3 py-2">
                <i className="bi bi-bar-chart-line me-2"></i>AI Analytics
              </span>
              <h1 className="display-4 fw-bold mb-4">
                AI That Predicts Student Performance
              </h1>
              <p className="lead mb-4 opacity-90">
                Don't wait for report cards to know a student is struggling.
                AI analyzes patterns in real-time and alerts you before grades drop.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Free Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Try Free
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg text-dark">
                <h6 className="fw-bold mb-3" style={{ color: '#5C2597' }}>Live Dashboard Preview</h6>
                {dashboardMetrics.map((metric, idx) => (
                  <div key={idx} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <div className="d-flex align-items-center">
                      <i className={`bi ${metric.icon} me-2`} style={{ color: '#5C2597' }}></i>
                      <span className="small">{metric.label}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <strong>{metric.value}</strong>
                      <span className={`badge ${metric.up ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.6rem' }}>
                        {metric.trend}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">AI Analytics Features</h2>
            <p className="text-muted lead">Turn student data into actionable intelligence</p>
          </div>
          <div className="row g-4">
            {analyticsFeatures.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Use Cases */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Real-World Impact</h2>
            <p className="text-muted lead">See how AI analytics makes a difference in actual schools</p>
          </div>
          <div className="row g-4">
            {useCases.map((uc, idx) => (
              <div key={idx} className="col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-header bg-white py-3" style={{ borderBottom: '3px solid #5C2597' }}>
                    <h5 className="mb-0 fw-bold">{uc.title}</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <span className="badge bg-light text-dark mb-1"><i className="bi bi-flag me-1"></i>Scenario</span>
                      <p className="small text-muted">{uc.scenario}</p>
                    </div>
                    <div className="mb-3">
                      <span className="badge text-white mb-1" style={{ backgroundColor: '#5C2597' }}><i className="bi bi-robot me-1"></i>AI Action</span>
                      <p className="small">{uc.aiAction}</p>
                    </div>
                    <div>
                      <span className="badge bg-success mb-1"><i className="bi bi-check-circle me-1"></i>Impact</span>
                      <p className="small text-success fw-semibold mb-0">{uc.impact}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What AI Analyzes */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">What AI Analyzes</h2>
            <p className="text-muted">Data points that feed into performance predictions</p>
          </div>
          <div className="row g-3 justify-content-center">
            {[
              'Exam scores across terms', 'Assignment completion rates', 'Attendance patterns',
              'Subject-wise trends', 'Teacher feedback', 'Peer comparison data',
              'Historical batch performance', 'Extra-curricular participation', 'Parent engagement level',
            ].map((item, idx) => (
              <div key={idx} className="col-6 col-md-4 col-lg-3">
                <div className="card border-0 shadow-sm text-center p-3 h-100">
                  <i className="bi bi-check-circle me-1 mb-1" style={{ color: '#5C2597' }}></i>
                  <small className="fw-semibold">{item}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Know Your Students Better with AI</h2>
          <p className="lead opacity-75 mb-4">Predict. Intervene. Improve. Before it's too late.</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
              <i className="bi bi-telephone me-2"></i>Book Free Demo
            </button>
            <button className="btn btn-outline-light btn-lg px-5" onClick={onNavigateToLogin}>
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AIStudentPerformanceAnalytics;
